import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { createAnimation, animations, animationKeyframes } from "./animations";

const MobileQRScannerContainer = styled.div`
  position: fixed;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  z-index: 1002;
  opacity: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  user-select: none;
  --qr-size: 165px;

  &.initial {
    animation: ${createAnimation(animations.qrScanner)};
  }

  &.move-to-position {
    animation: ${animationKeyframes.qrMoveToPosition} 1.5s
      cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
  }

  &.grow-and-blend {
    animation: ${createAnimation(animations.qrGrowAndBlend)};
    animation-fill-mode: forwards;
  }
`;

const MobileImageContainer = styled.div`
  position: relative;
  width: var(--qr-size);
  height: var(--qr-size);
  background-color: white;
  border-radius: 12px;
  padding: 8px;
  box-sizing: border-box;
  user-select: none;
`;

const MobileQRImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
  transition: all 0.5s ease-in-out;
  transform: ${(props) => (!props.$isAnimating ? "scale(1.2)" : "scale(1)")};
  user-select: none;
  pointer-events: none;
`;

const MobileQRScanner = ({
  onAnimationComplete,
  className,
  isNetworkError,
}) => {
  const [animationCount, setAnimationCount] = useState(0);
  const [isAnimating, setIsAnimating] = useState(true);
  const [isVisible, setIsVisible] = useState(true);
  const [currentClass, setCurrentClass] = useState("initial");
  const [maxAnimations, setMaxAnimations] = useState(1); // Initialize with minimum value

  // Calculate GIF duration (assuming it's 2 seconds, adjust as needed)
  const gifDuration = 2000; // 2 seconds

  // Generate weighted random number of animations
  useEffect(() => {
    if (isNetworkError) {
      // For network errors, always play once
      setMaxAnimations(1);
    } else {
      // For verified and unverified products, use weighted random
      const getWeightedRandom = () => {
        const random = Math.random();
        if (random < 0.66) return 1; // 66% chance for 1
        if (random < 0.99) return 2; // 33% chance for 2
        return 3; // 1% chance for 3
      };

      const randomAnimations = getWeightedRandom();
      setMaxAnimations(randomAnimations);
    }
  }, [isNetworkError]);

  useEffect(() => {
    if (animationCount < maxAnimations) {
      const timer = setTimeout(() => {
        setAnimationCount((prev) => prev + 1);
      }, gifDuration);

      return () => clearTimeout(timer);
    } else {
      setIsAnimating(false);
      // Notify parent component that animation is complete after a short delay
      const timer = setTimeout(() => {
        if (onAnimationComplete) {
          onAnimationComplete();
        }
      }, 500);

      return () => clearTimeout(timer);
    }
  }, [animationCount, onAnimationComplete, maxAnimations]);

  // Handle class changes
  useEffect(() => {
    if (className?.includes("move-to-position")) {
      setCurrentClass("move-to-position");
    } else if (className?.includes("grow-and-blend")) {
      setCurrentClass("grow-and-blend");
    }
  }, [className]);

  // Handle grow and blend animation completion
  useEffect(() => {
    if (currentClass === "grow-and-blend") {
      const timer = setTimeout(() => {
        setIsVisible(false);
      }, 1000); // Match the duration of the animation

      return () => clearTimeout(timer);
    }
  }, [currentClass]);

  if (!isVisible) {
    return null;
  }

  return (
    <MobileQRScannerContainer className={currentClass}>
      <MobileImageContainer>
        <MobileQRImage
          src={
            isAnimating ? "/images/qr-code.gif" : "/images/qr-code-static.png"
          }
          alt="QR Code"
          $isAnimating={isAnimating}
        />
      </MobileImageContainer>
    </MobileQRScannerContainer>
  );
};

export default MobileQRScanner;
